import { z } from "zod";
export const HealthResponseSchema = z.object({
    status: z.literal("ok"),
    timestamp: z.string(),
    env: z.string(),
});
// ── Journal ──────────────────────────────────────────────────────────────────
export const MoodSchema = z.enum(["great", "good", "okay", "bad", "awful"]);
export const CreateJournalEntrySchema = z.object({
    title: z.string().min(1).max(200),
    content: z.string().min(1),
    mood: MoodSchema.optional(),
});
export const UpdateJournalEntrySchema = z.object({
    title: z.string().min(1).max(200).optional(),
    content: z.string().min(1).optional(),
    mood: MoodSchema.nullable().optional(),
});
export const JournalEntrySchema = z.object({
    id: z.string(),
    userId: z.string(),
    title: z.string(),
    content: z.string(),
    mood: MoodSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ── Registration ─────────────────────────────────────────────────────────────
export const CompleteRegistrationSchema = z.object({
    name: z.string().min(1).max(100),
});
// ── Location ─────────────────────────────────────────────────────────────────
export const LocationSourceSchema = z.enum(["live", "significant", "one_shot"]);
export const LocationPointInputSchema = z.object({
    id: z.string().uuid(),
    timestamp: z.number().int(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    horizontalAccuracy: z.number(),
    altitude: z.number().nullable().optional(),
    verticalAccuracy: z.number().nullable().optional(),
    speed: z.number().nullable().optional(),
    speedAccuracy: z.number().nullable().optional(),
    course: z.number().nullable().optional(),
    courseAccuracy: z.number().nullable().optional(),
    floorLevel: z.number().int().nullable().optional(),
    source: LocationSourceSchema,
});
export const LocationVisitInputSchema = z.object({
    id: z.string().uuid(),
    arrival: z.number().int().nullable().optional(),
    departure: z.number().int().nullable().optional(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    horizontalAccuracy: z.number(),
});
// Supporting observations retain event clocks and original provider categories.
// They are not visit assertions or independent confirmations of GPS membership.
const ObservationTimestampSchema = z.number().int().min(0).max(8640000000000000);
export const ApplePoiQueryInputSchema = z.object({
    id: z.string().uuid(),
    queriedAt: ObservationTimestampSchema,
    queryLatitude: z.number().min(-90).max(90),
    queryLongitude: z.number().min(-180).max(180),
    radiusM: z.number().positive().max(5000),
    locationPointId: z.string().uuid().nullable().optional(),
    // Raw visit trigger identity, not a snapshot or assertion of occupancy.
    locationVisitId: z.string().uuid().nullable().optional(),
    measurementTimestamp: ObservationTimestampSchema.nullable().optional(),
    candidates: z
        .array(z.object({
        providerId: z.string().max(512).nullable().optional(),
        name: z.string().max(512).nullable().optional(),
        category: z.string().max(256).nullable().optional(),
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
    }))
        .max(20),
});
// Catalog context only: neither association is a claim of visit membership.
// A raw trigger's current interval is mutable, not a capture-time snapshot.
export const VisitCatalogEvidenceSchema = z.object({
    queries: z.array(ApplePoiQueryInputSchema.extend({
        association: z.enum(["raw_visit_overlap", "query_time_overlap"]),
        queryCenterDistanceM: z.number().nonnegative(),
        triggerInterval: z
            .object({ arrival: z.string().datetime(), departure: z.string().datetime() })
            .nullable(),
    })),
    // The bounded read may omit further context; an empty result is not absence proof.
    truncated: z.boolean(),
});
export const WifiObservationInputSchema = z.object({
    id: z.string().uuid(),
    observedAt: ObservationTimestampSchema,
    ssid: z.string().min(1).max(256),
    bssid: z.string().max(128).nullable().optional(),
    securityType: z.number().int().min(0).max(2147483647),
});
export const MotionActivityInputSchema = z.object({
    id: z.string().uuid(),
    startedAt: ObservationTimestampSchema,
    observedAt: ObservationTimestampSchema,
    stationary: z.boolean(),
    walking: z.boolean(),
    running: z.boolean(),
    automotive: z.boolean(),
    cycling: z.boolean(),
    unknown: z.boolean(),
    confidence: z.enum(["low", "medium", "high"]),
});
// Same-account temporal context only, not accepted detector support or occupancy.
// receivedAt is stored request-start time, not historical commit visibility.
const EvidenceIntervalSchema = z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
});
export const VisitWifiEvidenceSchema = z.object({
    visitId: z.string(),
    interval: EvidenceIntervalSchema,
    ongoing: z.boolean(),
    association: z.literal("occurrence_time_overlap"),
    detectorUse: z.literal("unknown"),
    observations: z
        .array(z.object({
        id: z.string(),
        observedAt: z.string().datetime(),
        receivedAt: z.string().datetime(),
        ssid: z.string(),
        securityType: z.number().int(),
    }))
        .max(200),
    truncated: z.boolean(),
});
export const InterVisitMovementEvidenceSchema = z.object({
    fromVisitId: z.string(),
    toVisitId: z.string(),
    interval: EvidenceIntervalSchema,
    association: z.literal("between_adjacent_visits"),
    // Activity starts do not establish durations or continuous collection coverage.
    coverage: z.literal("unknown"),
    observations: z
        .array(MotionActivityInputSchema.omit({
        id: true,
        startedAt: true,
        observedAt: true,
    }).extend({
        id: z.string(),
        startedAt: z.string().datetime(),
        observedAt: z.string().datetime(),
        receivedAt: z.string().datetime(),
    }))
        .max(200),
    truncated: z.boolean(),
});
// Sanity backstops to keep a single misbehaving client from posting an unbounded
// payload; raise if a real workload needs it.
export const LocationBatchInputSchema = z.object({
    deviceId: z.string().min(1).max(128),
    points: z.array(LocationPointInputSchema).max(1000),
    visits: z.array(LocationVisitInputSchema).max(200),
    applePoiQueries: z.array(ApplePoiQueryInputSchema).max(20).optional(),
    wifiObservations: z.array(WifiObservationInputSchema).max(200).optional(),
    motionActivities: z.array(MotionActivityInputSchema).max(200).optional(),
});
export const LocationBatchResponseSchema = z.object({
    acceptedPoints: z.number().int(),
    acceptedVisits: z.number().int(),
    // Old servers omit these: clients must retain rows unless their IDs appear.
    acknowledgedApplePoiQueryIds: z.array(z.string().uuid()).optional(),
    acknowledgedWifiObservationIds: z.array(z.string().uuid()).optional(),
    acknowledgedMotionActivityIds: z.array(z.string().uuid()).optional(),
});
export const GeocodeStatusSchema = z.enum(["pending", "ok", "no_result", "failed", "gave_up"]);
// Read shape for visits returned to clients (e.g. the web day view).
// Excludes raw provider payload + bookkeeping (attempts, place_response_raw)
// which aren't useful to render. Timestamps are ISO 8601 UTC strings
// (the server emits Date.toISOString()), so `.datetime()` keeps the contract
// explicit for consumers that validate responses against this schema.
export const LocationVisitSchema = z.object({
    id: z.string(),
    arrival: z.string().datetime().nullable(),
    departure: z.string().datetime().nullable(),
    latitude: z.number(),
    longitude: z.number(),
    horizontalAccuracy: z.number(),
    // What established the visit: 'clvisit' (Apple), 'gps' (dwell recovered from
    // the point stream — no place data until SMR-51), 'manual' (future).
    // Additive for clients; iOS decoders ignore unknown keys.
    source: z.enum(["clvisit", "gps", "manual"]).catch("clvisit"),
    reportedTimeAccuracy: z.enum(["exact", "approximate"]).nullable().optional(),
    // Optional for older payload fixtures; current servers always emit these.
    placeId: z.string().nullable().optional(),
    combinationId: z.string().nullable().optional(),
    placeName: z.string().nullable(),
    formattedAddress: z.string().nullable(),
    locality: z.string().nullable(),
    country: z.string().nullable(),
    countryCode: z.string().nullable(),
    placeTypes: z.array(z.string()).nullable(),
    geocodeStatus: GeocodeStatusSchema,
    // Actual accepted normalized-bin evidence, separate from detector radius
    // policy and venue naming. Neither accuracy nor count is a probability.
    positionEvidence: z
        .object({
        version: z.literal(1),
        acceptedBinCount: z.number().int().positive(),
        maxBinReportedAccuracyM: z.number().min(0).max(65),
        supportMs: z.number().nonnegative(),
        bridgedGapMs: z.number().nonnegative(),
    })
        .nullable()
        .optional(),
});
// Comparable observations from still-active, explicit single-visit assignments.
// These confirm the PRIOR visit, never the target visit. Bulk moves and restored
// assertions require separate lineage handling and are not included in v1.
export const VisitMembershipEvidenceSchema = z.object({
    version: z.literal(1),
    priors: z.array(z.object({
        visitId: z.string(),
        correctionId: z.string(),
        placeId: z.string(),
        assertedAt: z.string().datetime(),
        arrival: z.string().datetime(),
        departure: z.string().datetime(),
        latitude: z.number(),
        longitude: z.number(),
        horizontalAccuracy: z.number(),
        distanceM: z.number().nonnegative(),
    })),
    // Cap the 100 most recently departed priors before snapshot/spatial filtering;
    // this is not a globally nearest-prior result. Empty + truncated is unknown.
    truncated: z.boolean(),
});
// The context token is opaque and must be returned unchanged; a 409 means
// reload the sheet before deciding again. Fresh lookup candidates also carry
// a short-lived server-issued token; saved candidates need no candidate token.
export const GoogleAttributionSchema = z.object({
    provider: z.string().optional(),
    providerUri: z.string().optional(),
});
export const PersonalPlaceIdentitySchema = z.object({
    kind: z.enum(["custom", "candidate"]),
    name: z.string(),
    googlePlaceId: z.string().nullable(),
    formattedAddress: z.string().nullable(),
    placeTypes: z.array(z.string()).nullable(),
    attributions: z.array(GoogleAttributionSchema).optional(),
});
const NewPlaceSelectionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("custom"), label: z.string().trim().min(1).max(200) }),
    z.object({
        kind: z.literal("candidate"),
        candidateId: z.string().min(1),
        candidateToken: z.string().min(1).max(65536).optional(),
    }),
]);
const PlaceSelectionSchema = z.discriminatedUnion("kind", [
    ...NewPlaceSelectionSchema.options,
    z.object({ kind: z.literal("current") }),
]);
const CorrectionCommandFields = {
    operationId: z.string().uuid(),
    contextToken: z.string().min(1),
};
export const VisitCorrectionInputSchema = z.discriminatedUnion("scope", [
    z.object({
        ...CorrectionCommandFields,
        scope: z.literal("place"),
        selection: PlaceSelectionSchema,
    }),
    z.object({
        ...CorrectionCommandFields,
        scope: z.literal("visit"),
        target: z.discriminatedUnion("kind", [
            z.object({ kind: z.literal("existing"), placeId: z.string().min(1) }),
            z.object({ kind: z.literal("new"), selection: NewPlaceSelectionSchema }),
        ]),
    }),
]);
export const ReportedVisitPreviewInputSchema = z
    .object({
    target: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("visit"), visitId: z.string().min(1) }),
        z.object({ kind: z.literal("missing"), placeId: z.string().min(1) }),
    ]),
    arrival: z.string().datetime(),
    departure: z.string().datetime(),
    accuracy: z.enum(["exact", "approximate"]),
})
    .refine((v) => Number.isFinite(Date.parse(v.arrival)) &&
    Number.isFinite(Date.parse(v.departure)) &&
    Date.parse(v.arrival) < Date.parse(v.departure), { message: "Report a finite completed interval" });
export const ReportedVisitPreviewSchema = z.object({
    contextToken: z.string(),
    conflicts: z.array(z.object({
        id: z.string(),
        arrival: z.string().datetime(),
        departure: z.string().datetime().nullable(),
    })),
    current: z
        .object({
        arrival: z.string().datetime(),
        departure: z.string().datetime().nullable(),
        accuracy: z.enum(["exact", "approximate"]).nullable(),
        revision: z.number().int(),
        placeId: z.string().nullable(),
    })
        .nullable(),
    // Display snapshot from the same saved-place row bound to contextToken.
    savedPlace: z
        .object({
        id: z.string(),
        name: z.string().nullable(),
        formattedAddress: z.string().nullable(),
        latitude: z.number(),
        longitude: z.number(),
    })
        .nullable(),
});
export const ReportedVisitInputSchema = ReportedVisitPreviewInputSchema.safeExtend(CorrectionCommandFields);
export const ReportedVisitResultSchema = z.object({
    correctionId: z.string(),
    visitId: z.string(),
    removed: z.boolean(),
});
export const UndoCorrectionInputSchema = z.object(CorrectionCommandFields);
export const VisitCombinationPreviewInputSchema = z.object({
    visitIds: z.tuple([z.string().min(1), z.string().min(1)]),
});
export const VisitCombinationMemberSchema = z.object({
    id: z.string(),
    arrival: z.string().datetime(),
    departure: z.string().datetime(),
    accuracy: z.enum(["exact", "approximate"]).nullable(),
    revision: z.number().int(),
    placeId: z.string(),
});
export const VisitCombinationPreviewSchema = z.object({
    contextToken: z.string(),
    members: z.tuple([VisitCombinationMemberSchema, VisitCombinationMemberSchema]),
    savedPlace: ReportedVisitPreviewSchema.shape.savedPlace.unwrap(),
    gap: z.object({
        start: z.string().datetime(),
        end: z.string().datetime(),
        durationMs: z.number(),
    }),
    arrival: z.string().datetime(),
    departure: z.string().datetime(),
    accuracy: z.literal("approximate"),
});
export const VisitCombinationInputSchema = VisitCombinationPreviewInputSchema.extend({
    ...CorrectionCommandFields,
    continuityConfirmed: z.literal(true),
});
export const VisitCombinationResultSchema = z.object({
    combinationId: z.string(),
    visitId: z.string(),
    memberVisitIds: z.tuple([z.string(), z.string()]),
    undone: z.boolean(),
});
export const VisitCombinationUndoContextSchema = z.object({
    contextToken: z.string(),
    canUndo: z.boolean(),
    combinationId: z.string(),
    visitId: z.string(),
    members: z.tuple([VisitCombinationMemberSchema, VisitCombinationMemberSchema]),
});
export const CorrectionResultSchema = z.object({
    correctionId: z.string(),
    visitId: z.string(),
    scope: z.enum(["place", "visit"]),
    undoneCorrectionId: z.string().nullable(),
    // Undo never deletes a newly created personal place; it remains reusable.
    retainedPlaceId: z.string().nullable(),
});
export const CorrectionContextSchema = z.object({
    visitId: z.string(),
    placeId: z.string().nullable(),
    placeName: z.string().nullable(),
    placeAssigned: z.enum(["derived", "user"]),
    linkedVisitCount: z.number().int(),
    contextToken: z.string(),
    candidates: z.array(PersonalPlaceIdentitySchema.extend({ id: z.string() })),
    places: z.array(z.object({
        id: z.string(),
        name: z.string().nullable(),
        formattedAddress: z.string().nullable(),
        // Effective identity for explicit nearby-candidate reuse, not membership proof.
        // Missing on older servers; clients must not infer a match from names.
        googlePlaceId: z.string().nullable().optional(),
        manualOnly: z.boolean(),
        personalIdentityKind: PersonalPlaceIdentitySchema.shape.kind.nullable(),
        // Explicit personal identity or a nonblank legacy user-authored label.
        isPersonal: z.boolean(),
        latitude: z.number(),
        longitude: z.number(),
    })),
    corrections: z.array(z.object({
        id: z.string(),
        scope: z.enum(["place", "visit"]),
        createdAt: z.string().datetime(),
        // Snapshots, not today's mutable place labels. Null means unnamed/unassigned.
        beforeName: z.string().nullable(),
        afterName: z.string().nullable(),
        beforePlaceId: z.string().nullable(),
        afterPlaceId: z.string().nullable(),
        beforeAddress: z.string().nullable(),
        afterAddress: z.string().nullable(),
        undoneCorrectionId: z.string().nullable(),
        canUndo: z.boolean(),
        // Current undo impact, not the original correction's historical count.
        affectedVisitCount: z.number().int(),
    })),
});
// POST /api/location/visits/:id/correction-candidates takes no body/coordinates.
// Lookup does not assign or rename anything. Pass candidateToken inside the
// candidate selection of the existing /correct command after confirmation.
export const CorrectionCandidateLookupSchema = z.object({
    visitId: z.string(),
    candidates: CorrectionContextSchema.shape.candidates,
    candidateToken: z.string(),
    expiresAt: z.string().datetime(),
});
// A split moves an explicit set of visits, not a place's identity. It has a
// separate contract so already-shipped clients keep their two existing scopes.
export const ClusterSplitInputSchema = z.object({
    ...CorrectionCommandFields,
    visitIds: z
        .array(z.string().min(1))
        .min(1)
        .max(100)
        .refine((ids) => new Set(ids).size === ids.length, "Visit IDs must be unique")
        .transform((ids) => [...ids].sort()),
    target: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("existing"), placeId: z.string().min(1) }),
        z.object({ kind: z.literal("new"), label: z.string().trim().min(1).max(200) }),
    ]),
});
// A canonical merge confirms every previewed source member and an existing
// target. Unlike a subset split, it also retires the source identity.
export const ClusterMergeInputSchema = ClusterSplitInputSchema.extend({
    visitIds: z
        .array(z.string().min(1))
        .min(1)
        .refine((ids) => new Set(ids).size === ids.length, "Visit IDs must be unique")
        .transform((ids) => [...ids].sort()),
    target: z.object({ kind: z.literal("existing"), placeId: z.string().min(1) }),
});
export const ClusterCorrectionResultSchema = z.object({
    correctionId: z.string(),
    scope: z.literal("cluster"),
    sourcePlaceId: z.string(),
    targetPlaceId: z.string(),
    affectedVisitCount: z.number().int(),
    undoneCorrectionId: z.string().nullable(),
    retainedPlaceId: z.string().nullable(),
    retiredPlaceId: z.string().nullable().optional(),
});
export const ClusterCorrectionContextSchema = z.object({
    sourcePlaceId: z.string(),
    sourceName: z.string().nullable(),
    mergedIntoPlaceId: z.string().nullable().optional(),
    contextToken: z.string(),
    places: CorrectionContextSchema.shape.places,
    visits: z.array(z.object({
        id: z.string(),
        arrival: z.string().datetime(),
        departure: z.string().datetime().nullable(),
        placeAssigned: z.enum(["derived", "user"]),
    })),
    corrections: z.array(z.object({
        id: z.string(),
        createdAt: z.string().datetime(),
        beforeName: z.string().nullable(),
        afterName: z.string().nullable(),
        beforePlaceId: z.string(),
        afterPlaceId: z.string(),
        sourcePlaceId: z.string().optional(),
        visitIds: z.array(z.string()),
        affectedVisitCount: z.number().int(),
        undoneCorrectionId: z.string().nullable(),
        canUndo: z.boolean(),
        retiredPlaceId: z.string().nullable().optional(),
    })),
});
// Half-open window [from, to); the server returns visits overlapping it.
// `.datetime()` requires a trailing `Z` by default (offsets/local time are
// rejected), so the server's `new Date(from)` always parses an unambiguous
// UTC instant. The refine enforces from < to — an empty or inverted window is
// a client bug, not a valid 0-row query. Client builds local-day boundaries
// and sends UTC instants so the server stays TZ-naive.
export const LocationVisitsQuerySchema = z
    .object({
    from: z.string().datetime(),
    to: z.string().datetime(),
})
    .refine((q) => Date.parse(q.from) < Date.parse(q.to), {
    message: "from must be before to",
    path: ["from"],
});
export const VisitSourceSchema = z.enum(["clvisit", "inferred"]);
// ── Google Places + Geocoding shapes ─────────────────────────────────────────
// Two separate APIs with different conventions: Places API (New) uses camelCase
// + no top-level status field (errors via HTTP); Geocoding API uses snake_case
// + `lat`/`lng` + a `status` string. We mirror Google's wire formats so the
// raw responses round-trip to `location_visit.place_response_raw` verbatim.
// Places API (New) — POST https://places.googleapis.com/v1/places:searchNearby
export const GoogleLatLngSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
});
export const GoogleDisplayNameSchema = z.object({
    text: z.string(),
    languageCode: z.string().optional(),
});
// .loose() keeps unknown fields so the parsed object round-trips into
// place_response_raw without lossy stripping. Field-mask-controlled fields
// are still all optional() so missing data doesn't break parse.
export const GooglePlaceSchema = z
    .object({
    id: z.string(),
    displayName: GoogleDisplayNameSchema.optional(),
    types: z.array(z.string()).optional(),
    formattedAddress: z.string().optional(),
    location: GoogleLatLngSchema.optional(),
    attributions: z.array(GoogleAttributionSchema).optional(),
})
    .loose();
export const GoogleNearbySearchResponseSchema = z
    .object({
    places: z.array(GooglePlaceSchema).optional(),
})
    .loose();
// Geocoding API — GET https://maps.googleapis.com/maps/api/geocode/json
// (Older API surface; uses snake_case + `lat`/`lng`. Always returns HTTP 200;
// the `status` field carries the real outcome.)
export const GoogleGeocodeStatusSchema = z.enum([
    "OK",
    "ZERO_RESULTS",
    "OVER_DAILY_LIMIT",
    "OVER_QUERY_LIMIT",
    "REQUEST_DENIED",
    "INVALID_REQUEST",
    "UNKNOWN_ERROR",
]);
export const GoogleGeocodeGeometrySchema = z
    .object({
    location: z.object({ lat: z.number(), lng: z.number() }),
    location_type: z.string().optional(),
})
    .loose();
export const GoogleGeocodeResultSchema = z
    .object({
    place_id: z.string(),
    formatted_address: z.string(),
    types: z.array(z.string()).optional(),
    geometry: GoogleGeocodeGeometrySchema.optional(),
})
    .loose();
export const GoogleGeocodeResponseSchema = z
    .object({
    status: GoogleGeocodeStatusSchema,
    results: z.array(GoogleGeocodeResultSchema),
    error_message: z.string().optional(),
})
    .loose();
// ── Geocode queue message ────────────────────────────────────────────────────
// Wire format between producers (curate consumer, cron backstop) and the
// geocoder consumer. Per-PLACE since the SMR-51 rewire: resolution happens
// once per place row, not per raw visit. Place centroids are frozen, so no
// coord-drift guard is needed; the consumer checks resolution eligibility.
// Old in-flight {visitId} messages fail parse in the consumer and ack away.
export const GeocodeQueueMessageSchema = z.object({
    placeId: z.string(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    // Sentry distributed-trace propagation: producer attaches its current
    // span's `sentry-trace` + `baggage` header values; consumer continues
    // the trace so the visualization stitches end-to-end. Optional so
    // pre-existing in-flight messages from before this field shipped still
    // parse cleanly.
    _sentry: z
        .object({
        sentryTrace: z.string().optional(),
        baggage: z.string().optional(),
    })
        .optional(),
});
// Explicit kind keeps historical raw-visit geocode messages invalid. Coordinates
// are read from durable search state, never trusted from a queue payload.
export const VisitCandidateSearchMessageSchema = z.object({
    kind: z.literal("visit_candidates"),
    visitId: z.string(),
    fingerprint: z.string(),
});
// One per upload batch. The ids are raw `location_visit` (CLVisit) rows, NOT
// curated `visit` rows. The curate consumer cleans them into the silver `visit`
// layer and then enqueues the per-place geocode messages above.
export const CurateQueueMessageSchema = z.object({
    userId: z.string(),
    locationVisitIds: z.array(z.string()),
    // Same Sentry trace-propagation shape as GeocodeQueueMessage; optional so
    // pre-existing in-flight messages still parse.
    _sentry: z
        .object({
        sentryTrace: z.string().optional(),
        baggage: z.string().optional(),
    })
        .optional(),
});
export * from "./place-suggestions";
//# sourceMappingURL=index.js.map